import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const LegalContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}22 0%, ${props => props.theme.colors.secondary}22 100%);
  padding: ${props => props.theme.spacing.lg};
`;

const LegalCard = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: 0 4px 6px ${props => props.theme.colors.shadow};
  padding: ${props => props.theme.spacing.xl};
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  padding-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};

  h1 {
    color: ${props => props.theme.colors.primary};
    font-size: ${props => props.theme.typography.fontSize.xxxl};
    margin-bottom: ${props => props.theme.spacing.sm};
  }

  p {
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const Section = styled.section`
  margin-bottom: ${props => props.theme.spacing.xl};

  h2 {
    color: ${props => props.theme.colors.text.primary};
    font-size: ${props => props.theme.typography.fontSize.xl};
    margin-bottom: ${props => props.theme.spacing.md};
    padding-bottom: ${props => props.theme.spacing.sm};
    border-bottom: 2px solid ${props => props.theme.colors.primary};
  }

  h3 {
    color: ${props => props.theme.colors.text.primary};
    font-size: ${props => props.theme.typography.fontSize.lg};
    margin-top: ${props => props.theme.spacing.lg};
    margin-bottom: ${props => props.theme.spacing.sm};
  }

  p, li {
    color: ${props => props.theme.colors.text.secondary};
    line-height: 1.7;
    margin-bottom: ${props => props.theme.spacing.sm};
  }

  ul {
    padding-left: ${props => props.theme.spacing.lg};
    margin-bottom: ${props => props.theme.spacing.md};
  }
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-top: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const LastUpdated = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

export const Legal = () => {
  return (
    <LegalContainer>
      <LegalCard>
        <Header>
          <h1>NomoFarma</h1>
          <p>Términos y Condiciones / Política de Privacidad</p>
        </Header>

        <Section>
          <h2>1. Términos y Condiciones de Uso</h2>

          <h3>1.1 Aceptación de los Términos</h3>
          <p>
            Al acceder y utilizar la aplicación NomoFarma, usted acepta estar sujeto a estos
            términos y condiciones de uso. Si no está de acuerdo con alguna parte de estos términos,
            no podrá acceder al servicio.
          </p>

          <h3>1.2 Descripción del Servicio</h3>
          <p>
            NomoFarma es un sistema de gestión para farmacias que permite:
          </p>
          <ul>
            <li>Gestión de inventario de productos farmacéuticos</li>
            <li>Registro y seguimiento de ventas</li>
            <li>Administración de clientes</li>
            <li>Generación de reportes</li>
            <li>Control de gastos</li>
            <li>Alertas de stock y vencimientos</li>
          </ul>

          <h3>1.3 Uso Apropiado</h3>
          <p>
            El usuario se compromete a utilizar la aplicación de manera responsable y legal,
            respetando las normativas vigentes en materia de comercialización de productos farmacéuticos.
          </p>

          <h3>1.4 Cuenta de Usuario</h3>
          <p>
            El usuario es responsable de mantener la confidencialidad de su cuenta y contraseña.
            Todas las actividades que ocurran bajo su cuenta son su responsabilidad.
          </p>
        </Section>

        <Section>
          <h2>2. Política de Privacidad</h2>

          <h3>2.1 Información que Recopilamos</h3>
          <p>NomoFarma recopila la siguiente información:</p>
          <ul>
            <li><strong>Información de registro:</strong> nombre, correo electrónico y datos del negocio</li>
            <li><strong>Datos del negocio:</strong> inventario, ventas, clientes y gastos registrados por el usuario</li>
            <li><strong>Información técnica:</strong> tipo de dispositivo, sistema operativo y datos de uso de la aplicación</li>
          </ul>

          <h3>2.2 Uso de la Información</h3>
          <p>Utilizamos la información recopilada para:</p>
          <ul>
            <li>Proporcionar y mantener el servicio</li>
            <li>Mejorar la experiencia del usuario</li>
            <li>Enviar notificaciones importantes sobre el servicio</li>
            <li>Generar reportes y estadísticas para el usuario</li>
          </ul>

          <h3>2.3 Almacenamiento y Seguridad</h3>
          <p>
            Los datos se almacenan de forma segura en servidores de Google Firebase,
            utilizando encriptación y medidas de seguridad estándar de la industria.
            No compartimos su información personal con terceros sin su consentimiento.
          </p>

          <h3>2.4 Derechos del Usuario</h3>
          <p>El usuario tiene derecho a:</p>
          <ul>
            <li>Acceder a sus datos personales</li>
            <li>Rectificar datos incorrectos</li>
            <li>Solicitar la eliminación de su cuenta y datos</li>
            <li>Exportar sus datos en formato legible</li>
          </ul>

          <h3>2.5 Cookies y Tecnologías Similares</h3>
          <p>
            Utilizamos almacenamiento local del navegador para mantener su sesión activa
            y mejorar el rendimiento de la aplicación. No utilizamos cookies de seguimiento
            de terceros con fines publicitarios.
          </p>
        </Section>

        <Section>
          <h2>3. Limitación de Responsabilidad</h2>
          <p>
            NomoFarma se proporciona "tal cual" sin garantías de ningún tipo. No nos hacemos
            responsables por pérdidas de datos, interrupciones del servicio o cualquier daño
            derivado del uso de la aplicación. El usuario es responsable de mantener copias
            de seguridad de su información importante.
          </p>
        </Section>

        <Section>
          <h2>4. Modificaciones</h2>
          <p>
            Nos reservamos el derecho de modificar estos términos en cualquier momento.
            Los cambios serán notificados a través de la aplicación. El uso continuado
            del servicio después de dichos cambios constituye su aceptación de los nuevos términos.
          </p>
        </Section>

        <Section>
          <h2>5. Contacto</h2>
          <p>
            Para cualquier pregunta sobre estos términos o la política de privacidad,
            puede contactarnos a través de la aplicación o enviando un correo electrónico
            a soporte@nomofarma.com.
          </p>
        </Section>

        <LastUpdated>
          Última actualización: Enero 2026
        </LastUpdated>

        <BackLink to="/login">← Volver al inicio de sesión</BackLink>
      </LegalCard>
    </LegalContainer>
  );
};
